import { BotIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, CogIcon, FolderSearchIcon, LayoutGridIcon, LightbulbIcon, PencilRulerIcon, ShieldCheckIcon, ShuffleIcon, BookOpenTextIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// =====================================================================
// TEKNIKKLAR™ – all landing content (Swedish). Source of truth: teknikklar/PRODUCTO.md
// Images: WebP in Impultienda Recursos (store teknikklar 00c37bde…).
// =====================================================================

import { MOCKUP_URL, PAGE_URLS, pageTexts } from './teknikMedia';
export { MOCKUP_URL, PAGE_URLS };

// ---------- Images ----------
export const PRODUCT_IMAGE = MOCKUP_URL;
export const PRODUCT_IMAGE_ALT = 'TEKNIKKLAR-paketet: sex volymer i teknik för åk 1–9 med läsårsplan, lektioner, byggprojekt, ellaborationer, programmering, teknikutvecklingsarbetet och bedömning';

// ---------- Prices (Market Sverige, SEK) ----------
export const PRICE = '299 kr';
export const ORIGINAL_PRICE = '601 kr';

// ---------- Meta ----------
export const META = {
  title: 'TEKNIKKLAR™ – Färdiga tekniklektioner för åk 1–9',
  description: 'Färdigt 6-i-1-system för teknik åk 1–9: läsårsplan, 12 lektioner minut för minut, 18 byggprojekt, 12 säkra ellaborationer, 20 programmeringslektioner, teknikutvecklingsarbetet, bedömning och 5 bonusar.',
  ogTitle: 'TEKNIKKLAR™ – Gå in i teknikklassrummet med allt klart',
  ogDescription: 'Hela läsåret i teknik – färdigplanerat. Lektioner, byggprojekt, el, programmering och bedömning för åk 1–9. Omedelbar nedladdning.'
};

// ---------- Top bar (no price, no countdown) ----------
export const topBar = {
  long: 'Hela läsåret i teknik – färdigplanerat · Omedelbar nedladdning · 7 dagars nöjdhetsgaranti',
  short: 'Hela läsåret i teknik · 7 dagars garanti'
};

// ---------- Section copy ----------
export const copy = {
  ctaDefault: 'Skaffa TEKNIKKLAR™ nu',
  hero: {
    before: 'Få',
    highlight: 'hela läsåret',
    middle: 'i teknik färdigplanerat – kom igång på',
    accent: '5 minuter',
    sub: 'Gå in i teknikklassrummet med allt klart. Sex digitala volymer för åk 1–9 med färdiga lektioner, 18 byggprojekt, säkra ellaborationer med batteri, programmering med och utan dator, teknikutvecklingsarbetet och bedömning – plus fem bonusar.',
    badgeLine1: '6 volymer',
    badgeLine2: '+ 5 bonusar'
  },
  stats: { title: 'Det kompletta paketet i siffror' },
  difference: {
    kicker: 'Skillnaden med TEKNIKKLAR™',
    titleStart: 'Från improvisation i tekniken till',
    titleAccent: 'lugn och struktur.',
    intro: 'Tänk dig att börja veckan med teknikmomenten redan planerade. Du vet vad varje grupp ska bygga, koppla eller programmera – och du har materiallistan, stegen, säkerhetsreglerna och bedömningen klara.',
    beforeLabel: 'Innan TEKNIKKLAR™',
    beforeTitle: 'Känner du igen dig?',
    afterLabel: 'Med TEKNIKKLAR™',
    afterTitle: 'Planera mindre. Bygg mer.',
    closing: 'Fokusera på eleverna som bygger, testar och förbättrar – inte på att skapa allt material själv.'
  },
  recognize: {
    title: 'Har du någonsin upplevt det här?',
    text: 'Om du svarar ja på minst en fråga är TEKNIKKLAR™ för dig.',
    link: 'Du förtjänar ett enklare sätt'
  },
  inside: {
    title: 'Vad innehåller',
    brand: 'TEKNIKKLAR™',
    packTitle: 'Vad gör TEKNIKKLAR™ annorlunda?',
    packText: 'Ett helt läsårs material i teknik – konstruktioner, mekanismer, el, programmering och teknikutveckling – organiserat och redo att använda.'
  },
  pages: {
    kicker: 'Titta inuti TEKNIKKLAR™',
    titleStart: 'Se hur materialet ser ut',
    titleAccent: 'på insidan.',
    text: 'Riktiga sidor ur Volym 1: läsårsplan, lärarsidor minut för minut med mål, material och säkerhet, elevblad i tre nivåer och bedömning – skapade för teknikundervisningen.'
  },
  bonuses: {
    kicker: 'Men det finns mer!',
    titleStart: '5 bonusar',
    titleAccent: 'utan extra kostnad',
    text: 'Fem extra resurser ingår tillsammans med dina sex volymer – så att du kan organisera och genomföra teknikundervisningen ännu enklare.'
  },
  how: {
    titleStart: 'Så fungerar',
    brand: 'TEKNIKKLAR™',
    text: 'Från nedladdning till klassrummet: förbered nästa tekniklektion på bara fem minuter.',
    closing: 'Öppna → välj → förbered → bygg. Enkelt och strukturerat.'
  },
  audience: {
    titleStart: 'För vem är',
    brand: 'TEKNIKKLAR™',
    lead: 'Paketet är för dig som:',
    closing: 'Skapat för lärare som undervisar i teknik och vill lägga mindre tid på förberedelser och mer tid på eleverna.',
    asideTitle: 'Vad är din tid värd?',
    asideLead: 'Tänk på hur många timmar du lägger på planering i teknik varje vecka:',
    asideText: 'Tänk dig nu ett organiserat paket som hjälper dig med allt detta – under hela läsåret.',
    asideClosing: 'Du behöver inte fortsätta börja från noll.'
  },
  offer: {
    titleStart: 'Upptäck ditt kompletta',
    brand: 'TEKNIKKLAR™',
    titleEnd: '-paket',
    text: 'Hela läsåret i teknik – färdigplanerat. Kom igång på 5 minuter.',
    includesLabel: 'Det kompletta paketet innehåller',
    priceLabel: 'Introduktionspris',
    originalLabel: 'Ord. pris',
    oneTime: 'Engångsbetalning. Ingen prenumeration.',
    cta: 'Skaffa hela paketet nu'
  },
  guarantee: {
    title: '7 dagars nöjdhetsgaranti',
    text: 'Ladda ner materialet och se hur det fungerar i din teknikundervisning. Om paketet inte motsvarar dina förväntningar kan du kontakta oss via e-post och begära återbetalning inom 7 dagar från köpet, enligt våra garantivillkor.',
    legal: 'Garantin påverkar inte dina rättigheter enligt gällande konsumentlagstiftning.'
  },
  finalCta: {
    titleStart: 'Din teknikundervisning kan vara',
    titleAccent: 'redo redan idag.',
    text: 'I stället för att leta byggprojekt, testa kretsar och skriva programmeringsuppdrag på kvällarna har du ett organiserat paket i teknik för hela läsåret. Mer tid till det som verkligen betyder något:',
    closing: 'Dina elever. Din undervisning. Ditt liv utanför skolan.',
    recap: ['6 volymer', '5 bonusar', '240 sidor', '18 byggprojekt', 'Åk 1–9'],
    oneTime: 'Engångsbetalning – ingen prenumeration.',
    reassurances: ['Omedelbar digital nedladdning', 'Utskrivbara PDF-filer', '7 dagars nöjdhetsgaranti']
  },
  footer: {
    brand: 'TEKNIKKLAR',
    disclaimer: 'TEKNIKKLAR™ är ett fristående undervisningsmaterial i teknik för åk 1–9. Materialet bygger på det centrala innehållet i Lgr22. Använd bara batterier (1,5–9 V) och följ alltid skolans egna säkerhetsrutiner.',
    copyright: '© 2026 TEKNIKKLAR™ · edulabs. Alla rättigheter förbehållna.'
  }
};

// ---------- Data ----------
export const heroGuarantees = ['Omedelbar digital nedladdning', 'Engångsbetalning', 'Ingen prenumeration', '7 dagars nöjdhetsgaranti'];

export const stats = [{
  value: '240',
  label: 'Sidor i sex volymer'
}, {
  value: '12',
  label: 'Lektioner minut för minut'
}, {
  value: '18',
  label: 'Bygg- och konstruktionsprojekt'
}, {
  value: '20',
  label: 'Lektioner i programmering'
}, {
  value: '5',
  label: 'Bonusar som ingår'
}];

export const differenceStats = [{
  value: 'Åk 1–9',
  label: 'Läsårsplan för åk 1–3, 4–6 och 7–9'
}, {
  value: '6 digitala volymer',
  label: 'Lektioner, konstruktioner, el, programmering, teknikutveckling och bedömning'
}, {
  value: 'Utskrivbara PDF-filer',
  label: 'Att skriva ut till klassrummet eller visa digitalt'
}];

export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Söndagskvällen går åt till planering',
  text: 'Du letar byggprojekt, räknar ut material och testar instruktionerna – och veckan har inte ens börjat.'
}, {
  title: 'Teknik utan teknikutbildning',
  text: 'Du undervisar i teknik men har aldrig fått någon utbildning i ämnet – och måste uppfinna allt själv.'
}, {
  title: 'El känns osäkert',
  text: 'Du vill att eleverna kopplar kretsar, men är osäker på vad som är säkert och hur man förklarar serie- och parallellkoppling.'
}, {
  title: 'Programmering utan plan',
  text: 'Programmering ska ingå, men du har inga färdiga lektioner – och vet inte var du ska börja.'
}, {
  title: 'Teknikutvecklingsarbetet ska dokumenteras',
  text: 'Identifiera behov, skissa, konstruera, testa och värdera – men eleverna har ingen loggbok och du ingen mall.'
}, {
  title: 'Bedömningen tar kvällarna',
  text: 'Matriser, omdömen och betyg E/C/A i åk 6 och 9 blir ännu mer arbete efter skoltid.'
}];

export const afterItems = ['Ett organiserat paket i teknik för hela grundskolan', 'Läsårsplan för åk 1–3, 4–6 och 7–9', '12 lektioner planerade minut för minut', '18 byggprojekt med billigt material och steg för steg', '12 säkra ellaborationer med batteri', '20 programmeringslektioner – med och utan dator', 'Mallar för teknikutvecklingsarbetet', 'Bedömningsmatriser och 100 färdiga kommentarer'];

export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Kvällarna går åt till planering',
  text: 'Sitter du på kvällar och helger och planerar nästa veckas tekniklektioner?'
}, {
  icon: FolderSearchIcon,
  title: 'Du letar efter projekt som fungerar',
  text: 'Söker du ständigt efter byggprojekt med billigt material och rätt svårighetsgrad för just din årskurs?'
}, {
  icon: ShieldCheckIcon,
  title: 'Säkerheten måste sitta',
  text: 'Vill du ha tydliga säkerhetsregler för el, verktyg och limpistol som eleverna faktiskt följer?'
}, {
  icon: PencilRulerIcon,
  title: 'Du vill slippa skapa allt från noll',
  text: 'Önskar du att lektioner, elevblad, mallar och matriser redan var förberedda?'
}, {
  icon: ShuffleIcon,
  title: 'Eleverna arbetar i olika tempo',
  text: 'Behöver du uppgifter i tre nivåer – för elever som behöver stöd och för dem som vill ha mer utmaning?'
}, {
  icon: LayoutGridIcon,
  title: 'Hela kursplanen i samma system',
  text: 'Vill du ha konstruktioner, mekanismer, el, programmering och teknikhistoria samlat så att du snabbt hittar det du behöver?'
}];

// Spine colours mirror the official box (marinblå, laxrosa, teknikorange, grön, svensk blå, röd).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Volym 01',
  level: 'Läsårsplan',
  title: 'Läsårsplan + 12 lektioner minut för minut',
  tagline: 'Gå in i teknikklassrummet med allt klart.',
  intro: 'Den första volymen ger dig strukturen för hela läsåret och tolv färdiga lektioner på 60 minuter – med lärarsida och elevblad i tre nivåer.',
  items: ['Läsårsplan för åk 1–3, 4–6 och 7–9', 'Terminsplan vecka för vecka', 'Progressionstabell: konstruktioner, mekanismer, el, programmering och teknikutveckling', 'Materiallista: ”billigt teknikförråd”', '12 lektioner minut för minut (60 min)', 'Lärarsida med MÅL, MATERIAL och SÄKERHET', 'Elevblad i 3 nivåer', 'Quiz med facit och bedömningsmatris'],
  outro: 'Från ”Vad är teknik? Teknikjakt i klassrummet” i åk 1 till ”Lös ett verkligt problem i skolan” i åk 7–9.',
  spine: 'bg-[#1B2A5E] text-white',
  rule: 'border-white/30'
}, {
  number: 'Volym 02',
  level: 'Konstruktioner',
  title: 'Konstruktioner och mekanismer: 18 byggprojekt',
  tagline: 'Bygg, testa och förbättra – med billigt material.',
  intro: 'Den andra volymen innehåller 18 färdiga bygg- och konstruktionsprojekt med materiallista, tid, steg-för-steg, ”testa och förbättra”, vanliga fel och tre nivåer.',
  items: ['Papperstorn och kartongbil', 'Glasspinnebro och katapult', 'Vindsnurra och kran med vinsch', 'Hiss med remskiva och saxlyft', 'Ballongbil och vattenhjul', 'Fackverksbro med belastningstest', 'Mekanisk leksak med kam', 'Lyftanordning med hydraulik (sprutor)', 'Vindkraftverk, solugn och jordbävningssäker byggnad', 'Rullband och egen konstruktion'],
  outro: 'Hållfasta konstruktioner och mekanismer som eleverna kan ta på.',
  spine: 'bg-[#FF8A7A] text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 03',
  level: 'El & styrning',
  title: 'Elektricitet och styrning: 12 säkra ellaborationer',
  tagline: 'Bara batterier 1,5–9 V – tryggt och steg för steg.',
  intro: 'Den tredje volymen innehåller tolv säkra ellaborationer med batteri, kretsscheman med symboler och tydliga säkerhetsregler.',
  items: ['Sluten krets', 'Ledare och isolatorer', 'Strömbrytare av gem', 'Serie- och parallellkoppling', 'Ficklampa och trafikljus', 'Ståltrådsspel och larm', 'Elektromagnet', 'Motor och fläkt', 'Enkel styrning med givare', 'Elsäkerhet hemma'],
  outro: 'Lampan tänds bara när kretsen är sluten – och alla förstår varför.',
  spine: 'bg-[#F28C28] text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 04',
  level: 'Programmering',
  title: 'Programmering: 20 lektioner',
  tagline: '10 utan dator + 10 med blockprogrammering.',
  intro: 'Den fjärde volymen innehåller 20 programmeringslektioner med uppdragskort och facit. Fungerar med Scratch eller valfri gratis blockmiljö – micro:bit är ett tillval, aldrig ett krav.',
  items: ['Algoritmer och sekvens', 'Loopar och villkor', 'Felsökning med kort och rutnät', 'Styra en figur', 'Räknare och reaktionsspel', 'Styra ett trafikljus', 'Enkel mätning', 'Uppdragskort och facit'],
  outro: 'Programmering som alla kan undervisa i – även utan dyra inköp.',
  spine: 'bg-[#2BB673] text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 05',
  level: 'Teknikutveckling',
  title: 'Teknikutvecklingsarbetet och teknikhistoria',
  tagline: 'Från behov till lösning – synligt och dokumenterat.',
  intro: 'Den femte volymen leder eleverna genom teknikutvecklingsprocessen med mallar för dokumentation, och kopplar tekniken till människa, samhälle och miljö.',
  items: ['Identifiera behov → undersöka → föreslå lösningar', 'Konstruera → testa → värdera', 'Mallar för skiss, ritning och loggbok', 'Tekniska system: vatten, el, avlopp, internet och återvinning', 'Teknik, människa, samhälle och miljö', 'Teknikhistoria: hjulet, tryckpressen, ångmaskinen, glödlampan, telefonen och datorn', 'Svenska uppfinningar: kullagret, skiftnyckeln, blixtlåset och Celsius', 'Hållbarhet och livscykel'],
  outro: 'Processen blir lika viktig som den färdiga lösningen.',
  spine: 'bg-[#006AA7] text-white',
  rule: 'border-white/30'
}, {
  number: 'Volym 06',
  level: 'Bedömning',
  title: 'Bedömning',
  tagline: 'Gör bedömningen enklare och rättvisare.',
  intro: 'Den sjätte volymen innehåller färdigt stöd för bedömning, betyg och omdömen i teknik.',
  items: ['Bedömningsmatriser per område', 'Stöd för betyg E/C/A i åk 6 och 9', 'Observationsscheman', 'Självbedömning', 'Teknikloggbok', 'Prov A/B per stadium med facit', '100 färdiga kommentarer till omdömen och IUP'],
  outro: 'Mindre tid på bedömning – tydligare uppföljning.',
  spine: 'bg-[#C8323C] text-white',
  rule: 'border-white/30'
}];

export const volumesIntro = {
  title: '6 digitala volymer för åk 1–9',
  text: 'Ett komplett paket i teknik som hjälper dig att planera och genomföra undervisningen under hela läsåret. Varje volym innehåller 40 sidor med lektioner, byggprojekt, ellaborationer, programmering och bedömning.'
};

export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Lektioner minut för minut',
  text: 'Slipp börja från ett tomt dokument. 12 lektioner på 60 minuter med mål, material, säkerhet och elevblad i tre nivåer.'
}, {
  icon: CogIcon,
  title: '18 byggprojekt',
  text: 'Konstruktioner och mekanismer med billigt material – från papperstorn till vindkraftverk.'
}, {
  icon: LightbulbIcon,
  title: '12 säkra ellaborationer',
  text: 'Kretsar, strömbrytare och elektromagneter – bara med batterier på 1,5–9 V.'
}, {
  icon: BotIcon,
  title: '20 programmeringslektioner',
  text: '10 utan dator och 10 med blockprogrammering – utan krav på dyra inköp.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Bedömning E/C/A',
  text: 'Bedömningsmatriser, observationsscheman, prov A/B med facit och 100 färdiga kommentarer.'
}, {
  icon: CalendarCheckIcon,
  title: 'Läsårsplan',
  text: 'Läsårsplan för åk 1–3, 4–6 och 7–9, terminsplan vecka för vecka och progressionstabell.'
}];

export const pageSamples: Array<{ src: string; label: string; title: string; text: string }> = pageTexts.map((page, index) => ({
  ...page,
  src: PAGE_URLS[index] || ''
}));

export const pageTypes = ['Lektioner minut för minut', 'Elevblad i 3 nivåer', 'Säkerhet i varje lektion', 'Bedömningsmatriser'];

export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Teknikstart på 5 minuter',
  text: '40 korta uppstarter som får igång lektionen direkt – med facit.',
  items: ['10 ”Vad är det här för teknik?”', '10 ”Hur fungerar det?”', '10 snabba byggutmaningar', '10 ”Rätt eller fel” med facit'],
  benefit: 'Starta varje tekniklektion med fokus – utan extra förberedelse.'
}, {
  title: 'Vikariepärm för teknik',
  text: '15 lektioner som vikarien kan genomföra utan verktyg och utan el.',
  items: ['Skiss', 'Teknikhistoria', 'Programmering utan dator', 'Papperskonstruktioner', 'Instruktion, elevblad och facit'],
  benefit: 'Lugn och ro även när du inte kan vara på plats.'
}, {
  title: 'Föräldrakommunikation i teknik',
  text: 'Färdiga brev för kontakten med vårdnadshavare under hela läsåret.',
  items: ['Läsårsinformation', 'Insamling av återbruksmaterial', 'Säkerhetsinformation', 'Inbjudan till teknikdag', 'Mall för utvecklingssamtal'],
  benefit: 'Tydlig kommunikation på några minuter.'
}, {
  title: 'Teknikdagen – en färdig temadag för hela skolan',
  text: 'Allt du behöver för en teknikdag med stationer och tävlingar.',
  items: ['Stationer', 'Tidsplan', 'Materiallista', 'Tävlingsregler (bro- och tornutmaning)', 'Diplom', 'Checklista'],
  benefit: 'En hel temadag – utan stress.'
}, {
  title: 'Affischer för teknikklassrummet',
  text: '20 affischer som gör teknikklassrummet tydligt och säkert.',
  items: ['Teknikutvecklingsprocessen', 'Kretssymboler', 'Mekanismer', 'Konstruktioner: balk, fackverk och valv', 'Säkerhetsregler', 'Programmeringsbegrepp', 'Svenska uppfinningar'],
  benefit: 'Skriv ut och sätt upp – direkt.'
}];

export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Öppna TEKNIKKLAR™',
  text: 'Ladda ner paketet och öppna volymen du behöver – planering, konstruktioner, el, programmering, teknikutveckling eller bedömning.'
}, {
  title: 'Välj lektion eller projekt',
  text: 'Hitta rätt årskurs och välj lektion, byggprojekt eller elevblad i rätt nivå.'
}, {
  title: 'Förbered dig på 5 minuter',
  text: 'Gå igenom lärarsidan, materiallistan och säkerhetsrutan.'
}, {
  title: 'Skriv ut och bygg',
  text: 'Skriv ut elevbladen eller visa dem digitalt – och gå in i klassrummet med en tydlig plan.'
}];

export const audienceItems = ['Undervisar i teknik i åk 1–9', 'Är klasslärare och har teknik utan egen teknikutbildning', 'Vill ha ett helt läsårs material i teknik organiserat', 'Vill ha färdiga byggprojekt med billigt material', 'Vill ha säkra ellaborationer och tydliga säkerhetsregler', 'Vill undervisa i programmering utan dyra inköp', 'Behöver uppgifter i tre nivåer', 'Vill ha färdiga bedömningsmatriser och omdömen'];

export const timeValueTasks = ['Leta byggprojekt med rätt svårighetsgrad', 'Testa kretsar och skriva säkerhetsregler', 'Planera programmeringslektioner', 'Göra elevblad och mallar för loggboken', 'Bedöma och skriva omdömen'];

export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digitala volymer – 240 sidor',
  detail: 'Läsårsplan och lektioner, konstruktioner och mekanismer, el och styrning, programmering, teknikutvecklingsarbetet och bedömning'
}, {
  label: '5 bonusar utan extra kostnad',
  detail: 'Teknikstart, vikariepärm, föräldrakommunikation, teknikdagen och affischer'
}, {
  label: 'För åk 1–9',
  detail: '12 lektioner minut för minut, 18 byggprojekt, 12 ellaborationer, 20 programmeringslektioner och 100 färdiga kommentarer'
}];

export const offerIncludes = ['Omedelbar digital nedladdning', 'PDF-filer att spara', 'Utskrivbara elevblad', 'Ingen fysisk produkt skickas'];

// No buyers yet: only add authentic reviews of TEKNIKKLAR. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];

export const faqs = [{
  q: 'Hur får jag materialet?',
  a: 'TEKNIKKLAR™ levereras digitalt. Efter köpet får du tillgång till PDF-filer som du kan ladda ner och spara. Du kan använda materialet på dator, surfplatta eller mobil och skriva ut de sidor du behöver.'
}, {
  q: 'Vad ingår i de sex volymerna?',
  a: 'Volym 1: läsårsplan och 12 lektioner minut för minut. Volym 2: konstruktioner och mekanismer – 18 byggprojekt. Volym 3: elektricitet och styrning – 12 säkra ellaborationer. Volym 4: programmering – 20 lektioner. Volym 5: teknikutvecklingsarbetet och teknikhistoria. Volym 6: bedömning.'
}, {
  q: 'Vilka årskurser passar det för?',
  a: 'TEKNIKKLAR™ är för åk 1–9. Du får läsårsplaner för åk 1–3, 4–6 och 7–9, och lektioner och projekt för alla stadier.'
}, {
  q: 'Jag har ingen teknikutbildning. Fungerar det ändå?',
  a: 'Ja. Varje lektion har en lärarsida med mål, material, säkerhet och genomgång minut för minut, så att du vet exakt vad du ska göra – även om du inte har undervisat i teknik tidigare.'
}, {
  q: 'Bygger materialet på Lgr22?',
  a: 'Ja. TEKNIKKLAR™ bygger på det centrala innehållet i kursplanen i teknik i Lgr22: tekniska lösningar, arbetsmetoder för utveckling av tekniska lösningar samt teknik, människa, samhälle och miljö. TEKNIKKLAR™ är ett fristående undervisningsmaterial.'
}, {
  q: 'Hur hanteras säkerheten?',
  a: 'Varje lektion har en SÄKERHET-ruta. Ellaborationerna görs bara med batterier på 1,5–9 V – aldrig med vägguttag – och eleverna lär sig att aldrig kortsluta ett batteri. Limpistol används på låg temperatur vid en lärarstation, skyddsglasögon används vid sågning och borrning, och lödning görs bara i åk 7–9 tillsammans med läraren. Följ alltid skolans egna rutiner.'
}, {
  q: 'Behöver jag köpa dyrt material?',
  a: 'Nej. Byggprojekten använder billigt material och återbruk som kartong, glasspinnar, sugrör och snöre. Programmeringen fungerar utan dator eller med valfri gratis blockmiljö – micro:bit är ett tillval, aldrig ett krav.'
}, {
  q: 'Kan jag verkligen förbereda en lektion på 5 minuter?',
  a: 'TEKNIKKLAR™ är utformat för att göra förberedelserna snabbare. Du öppnar en volym, väljer en färdig lektion eller ett projekt och går igenom lärarsidan på ungefär fem minuter. Material behöver förstås finnas på plats, och tiden kan variera beroende på grupp.'
}, {
  q: 'Kan jag använda materialet för elever på olika nivåer?',
  a: 'Ja. Elevbladen finns i tre nivåer – stöd, standard och utmaning – så att alla elever kan arbeta mot samma mål.'
}, {
  q: 'Ingår facit?',
  a: 'Ja. Quiz, prov A/B, uppdragskort och uppstarter har facit, och paketet innehåller bedömningsmatriser, självbedömning och 100 färdiga kommentarer till omdömen.'
}, {
  q: 'Ingår de fem bonusarna?',
  a: `Ja. Alla fem bonusar ingår i paketet utan extra kostnad – allt för ${PRICE}.`
}, {
  q: 'Är det en prenumeration?',
  a: 'Nej. Du betalar en gång och får tillgång till det digitala materialet. Inga återkommande avgifter.'
}, {
  q: 'Är filerna utskrivbara?',
  a: 'Ja. Allt levereras som utskrivbara PDF-filer. Du väljer själv vilka sidor du skriver ut, och du kan även visa materialet digitalt.'
}, {
  q: 'Finns det någon garanti?',
  a: 'Ja. TEKNIKKLAR™ har 7 dagars nöjdhetsgaranti. Om materialet inte motsvarar dina förväntningar kan du kontakta oss och begära återbetalning inom 7 dagar från köpet enligt villkoren.'
}];

export const footerLinks = ['Köpvillkor', 'Integritetspolicy', 'Återbetalningspolicy', 'Kontakt'];
